/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import ChipsReadonly from '../../../../../../Form/ChipsReadonly';
import ChipsDropdown from '../../../../../../Form/ChipsDropdown';
import DocUploader from './DocUploader';
import MediaUploader from './MediaUploader';
import AdminService from '../../../../../../../services/AdminService';
import FormDropdown from '../../../../../../Form/FormDropdown';
import TextInput from '../../../../../../Form/TextInput';
import Textarea from '../../../../../../Form/Textarea';
import MultiDropdown from '../../../../../../Form/MultiDropdown';
import Checkbox from '../../../../../../Form/Checkbox';

import classes from './styles.module.scss';
import getResizedImageUrl from '../../../../../../../helpers/getResizedImageUrl';
import { useUserStore } from '../../../../../../../store';

export default function BasicInfo({
  formik,
  data,
  isTriedToSubmit,
  isEdit,
  readOnly,
  sourceCompanyId = null,
}) {
  const { t } = useTranslation();

  const lastPrefilledCompanyIdRef = useRef(null);
  const previousCompanyIdRef = useRef(null);
  const { isInterviewer, isClient } = useUserStore();
  const { data: restaurants } = useQuery({
    queryKey: ['locationsOptionsBasicInfo', formik?.values.company?.value],
    queryFn: () =>
      AdminService.getRestaurants({
        Filters: formik?.values.company?.value
          ? `company.id:eq:${formik.values.company.value}`
          : null,
        pageSize: 99999,
      }),
  });

  const { data: selectedCompanyPage } = useQuery({
    queryKey: ['selectedCompanyBasicInfo', formik?.values?.company?.value],
    queryFn: () =>
      AdminService.getCompanies({
        pageNumber: 1,
        pageSize: 1,
        Filters: `id:eq:${formik.values.company.value}`,
      }),
    enabled: !!formik?.values?.company?.value,
  });

  const { data: companyManagers } = useQuery({
    queryKey: ['companyManagersBasicInfo', formik?.values?.company?.value],
    queryFn: () =>
      AdminService.getCompanyManagers({
        id: formik.values.company.value,
      }),
    enabled: !!formik?.values?.company?.value,
  });

  // Fetch source company data when sourceCompanyId is provided
  const { data: sourceCompanyPage } = useQuery({
    queryKey: ['sourceCompanyBasicInfo', sourceCompanyId],
    queryFn: () =>
      AdminService.getCompanies({
        pageNumber: 1,
        pageSize: 1,
        Filters: `id:eq:${sourceCompanyId}`,
      }),
    enabled: !!sourceCompanyId && !readOnly,
  });

  // Reset restaurant options if currenly selected restaurants are not in the city
  useEffect(() => {
    if (formik?.values.locationIds && restaurants) {
      const rests = formik?.values.locationIds.map((loc) =>
        restaurants?.data?.find((rest) => rest.id === loc.value)
      );

      if (rests.includes(undefined)) {
        formik.setFieldValue('locationIds', []);
      }
    }
  }, [formik, formik?.values.locationIds, restaurants]);

  // Preselect first location when company is selected and no location chosen yet
  useEffect(() => {
    if (!formik || readOnly) {
      return;
    }

    const companySelected = !!formik?.values?.company?.value;
    const hasLocationSelection = Array.isArray(formik?.values?.locationIds)
      ? formik.values.locationIds.length > 0
      : false;

    if (companySelected && restaurants?.data?.length && !hasLocationSelection) {
      const firstLocation = restaurants.data[0];
      formik.setFieldValue('locationIds', [
        { label: firstLocation.name, value: firstLocation.id },
      ]);
    }
  }, [
    formik,
    restaurants,
    readOnly,
    formik?.values?.company,
    formik?.values?.locationIds,
  ]);

  // Clear locations and company fields if company is cleared
  useEffect(() => {
    if (!formik || readOnly) {
      return;
    }

    const companyId = formik?.values?.company?.value;

    if (!companyId && formik?.values?.locationIds?.length) {
      formik.setFieldValue('locationIds', [], false);
    }

    if (!companyId) {
      if (formik?.values?.companyJoiningNote) {
        formik.setFieldValue('companyJoiningNote', '', false);
      }
      if (formik?.values?.companyDescription) {
        formik.setFieldValue('companyDescription', '', false);
      }
      if (formik?.values?.companyManager) {
        formik.setFieldValue('companyManager', null, false);
      }
      lastPrefilledCompanyIdRef.current = null;
    }
  }, [formik, readOnly, formik?.values?.company?.value]);

  // Clear company manager when company changes
  useEffect(() => {
    if (!formik || readOnly) {
      return;
    }

    const currentCompanyId = formik?.values?.company?.value;
    const previousCompanyId = previousCompanyIdRef.current;

    // Only clear manager if company ID actually changed (not just field updates)
    if (previousCompanyId !== null && previousCompanyId !== currentCompanyId) {
      if (formik?.values?.companyManager) {
        formik.setFieldValue('companyManager', null, false);
      }
    }

    // Update the previous company ID reference
    previousCompanyIdRef.current = currentCompanyId;
  }, [formik, readOnly, formik?.values?.company?.value]);

  // Preselect company when sourceCompanyId is provided
  useEffect(() => {
    if (!formik || readOnly || !sourceCompanyId) {
      return;
    }

    // Only preselect if no company is already selected
    if (formik?.values?.company?.value) {
      return;
    }

    const sourceCompany = sourceCompanyPage?.data?.[0];
    if (!sourceCompany) {
      return;
    }

    // Set the company value in the expected format
    formik.setFieldValue(
      'company',
      {
        label: sourceCompany.name,
        value: sourceCompany.id,
      },
      false
    );
  }, [
    formik,
    readOnly,
    sourceCompanyId,
    sourceCompanyPage,
    formik?.values?.company?.value,
  ]);

  // Prefill company fields when company selection changes (only once per company id)
  useEffect(() => {
    if (!formik || readOnly) {
      return;
    }

    const companyId = formik?.values?.company?.value;
    if (!companyId) {
      return;
    }

    if (lastPrefilledCompanyIdRef.current === companyId) {
      return;
    }

    const company = selectedCompanyPage?.data?.[0];
    if (!company) {
      return;
    }

    const newNote = company?.companyJoiningNote || '';
    const newDesc = company?.companyDescription || '';

    if (formik?.values?.companyJoiningNote !== newNote) {
      formik.setFieldValue('companyJoiningNote', newNote, false);
    }
    if (formik?.values?.companyDescription !== newDesc) {
      formik.setFieldValue('companyDescription', newDesc, false);
    }

    lastPrefilledCompanyIdRef.current = companyId;
  }, [formik, readOnly, selectedCompanyPage, formik?.values?.company?.value]);

  const changeVideo = useCallback((file) => {
    formik?.setFieldValue('jobPostVideoId', file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let jobTitleWarning;
  let corporateTitleWarning;
  let coverFileNameError;

  if (formik?.values.jobTitle?.length < 5 && formik?.values.jobTitle) {
    jobTitleWarning = t(
      'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.TitleTooShortError'
    );
  }

  if (
    formik?.values.corporateTitle?.length < 2 &&
    formik?.values.corporateTitle
  ) {
    corporateTitleWarning = t(
      'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CorporateTitleTooShortError'
    );
  }

  if (formik?.values?.coverFileName?.name?.endsWith('.svg')) {
    coverFileNameError = t(
      'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.WrongPictureFormatError'
    );
    formik.setErrors({ coverFileName: coverFileNameError });
  }

  let jobOwnerField = null;

  if (formik && 'userId' in formik?.values) {
    jobOwnerField = (
      <FormDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobOwnerLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobOwnerPlaceholder'
        )}
        value={formik?.values.userId}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="userId"
        error={formik?.errors.userId}
        touched={isTriedToSubmit}
        fetchOptions={{
          dataName: 'usersOptions',
          getDataHandler: AdminService.getUsers,
          labelName: 'userName',
          isUserList: true,
        }}
      />
    );
  } else {
    <TextInput
      readonly
      label={t(
        'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobOwnerLabel'
      )}
      height={45}
      value={data?.user?.name}
    />;
  }

  const jobPostTypeField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypeLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypePlaceholder'
        )}
        value={formik?.values.jobPostType}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="jobPostType"
        options={[
          {
            value: 'Internal',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypeOptions.Internal'
            ),
          },
          {
            value: 'External',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypeOptions.External'
            ),
          },
        ]}
        touched={isTriedToSubmit}
        error={formik?.errors.jobPostType}
      />
    ) : (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypeLabel'
        )}
        height={45}
        readonly
        value={
          data?.jobPostType
            ? t(
                `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobPostTypeOptions.${data?.jobPostType}`
              )
            : ''
        }
      />
    );
  };

  const jobTitleField = () => {
    return formik ? (
      <TextInput
        readonly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTitleLabel'
        )}
        height={45}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTitlePlaceholder'
        )}
        value={formik?.values.jobTitle}
        onChange={formik?.handleChange}
        error={
          formik?.errors.jobTitle !==
            t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.TitleTooShortError'
            ) && formik?.errors.jobTitle
        }
        warning={jobTitleWarning}
        name="jobTitle"
        touched={isTriedToSubmit}
      />
    ) : (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTitleLabel'
        )}
        height={45}
        value={data.jobTitle}
        readonly
      />
    );
  };

  const corporateTitleField = () => {
    return formik ? (
      <TextInput
        readonly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CorporateTitleLabel'
        )}
        height={45}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CorporateTitlePlaceholder'
        )}
        value={formik?.values.corporateTitle}
        onChange={formik?.handleChange}
        // error={formik?.errors.corporateTitle}
        name="corporateTitle"
        touched={isTriedToSubmit}
        warning={corporateTitleWarning}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.May be added for RSC roles'
        )}
        infoWidth={171}
        note={t('common.optional')}
      />
    ) : (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CorporateTitleLabel'
        )}
        height={45}
        value={data.corporateTitle}
        name="corporateTitle"
        readonly
      />
    );
  };

  const jobTypeField = () => {
    return formik ? (
      <MultiDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTypeLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.pickJobType'
        )}
        value={formik?.values.jobTypeIds}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="jobTypeIds"
        width="100%"
        touched={isTriedToSubmit}
        error={formik?.errors.jobTypeIds}
        fetchOptions={{
          dataName: 'jobTypesOptionsBasicInfo',
          getDataHandler: AdminService.getJobTypes,
        }}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTypeLabel'
        )}
        height={45}
        value={data?.jobTypes.map((type) => type.name).join(', ')}
      />
    );
  };

  const jobLocationTypeField = () => {
    return formik ? (
      <MultiDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobLocationTypeLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.pickJobLocationType'
        )}
        value={formik?.values.jobLocationTypeIds}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="jobLocationTypeIds"
        width="100%"
        touched={isTriedToSubmit}
        error={formik?.errors.jobLocationTypeIds}
        fetchOptions={{
          dataName: 'jobLocationTypesOptionsBasicInfo',
          getDataHandler: AdminService.getJobLocationTypes,
          fetchHanlderOptions: {
            orderBy: 'sortOrder',
            sortOrder: 'asc',
          },
        }}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobTypeLabel'
        )}
        height={45}
        value={data?.jobTypes.map((type) => type.name).join(', ')}
      />
    );
  };

  const jobDescriptionField = () => {
    return formik ? (
      <Textarea
        readonly={readOnly}
        height={186}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobDescriptionLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobDescriptionPlaceholder'
        )}
        value={formik?.values.jobDescription}
        onChange={formik?.handleChange}
        error={formik?.errors.jobDescription}
        name="jobDescription"
        touched={isTriedToSubmit}
        maxLength={5000}
        errorStyle={{ position: 'absolute', bottom: -21 }}
      />
    ) : (
      <Textarea
        height={186}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobDescriptionLabel'
        )}
        readonly
        value={data.jobDescription}
      />
    );
  };

  const educationField = () => {
    return formik ? (
      <FormDropdown
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.EducationLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.EducationPlaceholder'
        )}
        fetchOptions={{
          dataName: 'educationOptionsBasicInfo',
          getDataHandler: AdminService.getEducations,
        }}
        value={formik?.values.graduation}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="graduation"
        touched={isTriedToSubmit}
        error={formik?.errors.graduation}
        height={45}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.EducationLabel'
        )}
        height={45}
        value={data?.graduation?.name}
      />
    );
  };

  const experienceField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.ExperienceLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.ExperiencePlaceholder'
        )}
        fetchOptions={{
          dataName: 'experienceOptionsBasicInfo',
          getDataHandler: AdminService.getWorkExperience,
        }}
        value={formik?.values.workExperienceId}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="workExperienceId"
        touched={isTriedToSubmit}
        error={formik?.errors.workExperienceId}
      />
    ) : (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.ExperienceLabel'
        )}
        height={45}
        value={data?.workExperience?.name}
        readonly
      />
    );
  };

  const skillField = () => {
    return formik && !readOnly ? (
      <ChipsDropdown
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.KeySkillsLabel'
        )}
        maxLength={100}
        height={186}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SkillsNeededInfo'
        )}
        value={formik?.values.skillIds}
        setFieldValue={formik?.setFieldValue}
        name="skillIds"
        infoWidth={137}
        searchInfo={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SkillsNeededSearchInfo'
        )}
        searchInfoWidth={186}
        fetchOptions={{
          dataName: 'skillsOptionsBasicInfo',
          getDataHandler: ({ pageNumber, term }) =>
            AdminService.getSkills({
              orderBy: 'sortOrder',
              sortOrder: 'asc',
              term,
              pageNumber,
            }),
        }}
        touched={isTriedToSubmit}
        error={formik?.errors.skillIds}
        errorStyle={{ position: 'absolute', bottom: -27 }}
      />
    ) : (
      <ChipsReadonly
        values={
          formik?.values.skillIds || data?.skills?.map((skill) => skill.name)
        }
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.KeySkillsLabel'
        )}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SkillsNeededInfo'
        )}
        infoWidth={137}
      />
    );
  };

  const companyField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyPlaceholder'
        )}
        value={formik?.values.company}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="company"
        error={formik?.errors.company}
        touched={isTriedToSubmit}
        fetchOptions={{
          dataName: 'companiesBasicInfo',
          getDataHandler: AdminService.getCompanies,
          fetchHanlderOptions: {
            Filters: `type:eq:Employer`,
          },
        }}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyLabel'
        )}
        height={45}
        value={data?.company?.name}
      />
    );
  };

  const companyManagerField = () => {
    if (!formik?.values?.company && !data?.companyManager) {
      return null;
    }

    const managerOptions =
      companyManagers?.map((manager) => ({
        value: manager.id,
        label:
          `${manager.firstName} ${manager.lastName}`.trim() || manager.email,
        companyId: manager.companyId,
      })) || [];

    return formik ? (
      <FormDropdown
        height={45}
        fixed
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyManagerLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyManagerPlaceholder'
        )}
        note={t('common.optional')}
        value={formik?.values.companyManager}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="companyManager"
        error={formik?.errors.companyManager}
        touched={isTriedToSubmit}
        options={managerOptions}
      />
    ) : (
      <TextInput
        readonly
        note={t('common.optional')}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.CompanyManagerLabel'
        )}
        height={45}
        value={
          data?.companyManager?.firstName || data?.companyManager?.lastName
            ? `${data?.companyManager?.firstName} ${data?.companyManager?.lastName}`.trim()
            : data?.companyManager?.email || ''
        }
      />
    );
  };

  const companyLocationFormField = () => {
    return (
      formik &&
      formik?.values?.company && (
        <MultiDropdown
          height={45}
          readOnly={readOnly}
          label={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.LocationLabel'
          )}
          placeholder={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.LocationPlaceholder'
          )}
          fetchOptions={{
            dataName: 'locationsOptionsBasicInfo',
            getDataHandler: AdminService.getRestaurants,
            fetchHanlderOptions: {
              Filters: formik?.values?.company?.value
                ? `company.id:eq:${formik.values.company.value}`
                : null,
            },
          }}
          info={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company location for the job'
          )}
          infoWidth={151}
          value={formik?.values.locationIds}
          setFieldValue={formik?.setFieldValue}
          setFieldTouched={formik?.setFieldTouched}
          name="locationIds"
          width="100%"
          touched={isTriedToSubmit}
          error={formik?.errors.locationIds}
          searchInfo={t('common.addNewLocation')}
          searchInfoWidth={188}
        />
      )
    );
  };

  const companyLocationLabelField = () => {
    return (
      !formik && (
        <TextInput
          label={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.LocationLabel'
          )}
          height={45}
          value={data?.locations?.map((loc) => loc.name).join(', ')}
          readonly
        />
      )
    );
  };

  const companyDescriptionField = () => {
    return formik ? (
      <Textarea
        readonly={readOnly}
        height={186}
        maxLength={5000}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Description'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Description Here'
        )}
        value={formik?.values.companyDescription}
        onChange={formik?.handleChange}
        error={formik?.errors.companyDescription}
        name="companyDescription"
        touched={isTriedToSubmit}
        note={t('common.optional')}
      />
    ) : (
      <Textarea
        height={186}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Description'
        )}
        readonly
        value={data.companyDescription}
      />
    );
  };

  const companyJoiningNoteField = () => {
    return formik ? (
      <Textarea
        readonly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Joining Note'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Joining Note Here'
        )}
        value={formik?.values.companyJoiningNote}
        onChange={formik?.handleChange}
        error={formik?.errors.companyJoiningNote}
        name="companyJoiningNote"
        touched={isTriedToSubmit}
        height={186}
        maxLength={5000}
        note={t('common.optional')}
      />
    ) : (
      <Textarea
        height={186}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Company Joining Note'
        )}
        readonly
        value={data.companyJoiningNote}
      />
    );
  };

  const hideCompanyFormField = () => {
    return (
      formik &&
      formik?.values?.company && (
        <Checkbox
          readonly={readOnly}
          label={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.HideEmployerInfoLabel'
          )}
          isSelected={!!formik?.values?.hideCompany}
          onChange={() =>
            formik?.setFieldValue('hideCompany', !formik?.values?.hideCompany)
          }
        />
      )
    );
  };

  const hideCompanyLabelField = () => {
    return (
      !formik && (
        <Checkbox
          readonly
          label={t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.HideEmployerInfoLabel'
          )}
          isSelected={!!data?.hideCompany}
          onChange={() => {}}
        />
      )
    );
  };

  const salaryTypeField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypePlaceholder'
        )}
        value={formik?.values.salaryType}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="salaryType"
        defaultValue={{
          value: 'Range',
          label: t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.Range'
          ),
        }}
        options={[
          {
            value: 'Range',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.Range'
            ),
          },
          {
            value: 'Fixed',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.Fixed'
            ),
          },
          {
            value: 'Negotiable',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.Negotiable'
            ),
          },
        ]}
        touched={isTriedToSubmit}
        error={formik?.errors.salaryType}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeLabel'
        )}
        height={45}
        value={
          data?.salaryType
            ? t(
                `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.${data?.salaryType}`
              )
            : ''
        }
      />
    );
  };

  const salaryFrequencyField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyPlaceholder'
        )}
        value={formik?.values.salaryFrequency}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="salaryFrequency"
        options={[
          {
            value: 'Hour',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Hour'
            ),
          },
          {
            value: 'Day',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Day'
            ),
          },
          {
            value: 'Week',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Week'
            ),
          },
          {
            value: 'Month',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Month'
            ),
          },
          {
            value: 'Annual',
            label: t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.Annual'
            ),
          },
        ]}
        touched={isTriedToSubmit}
        error={formik?.errors.salaryFrequency}
      />
    ) : (
      <TextInput
        readonly
        value={
          data?.salaryType
            ? t(
                `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.${data?.salaryFrequency}`
              )
            : ''
        }
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyLabel'
        )}
      />
    );
  };

  const salaryCurrencyCodeField = () => {
    return formik ? (
      <FormDropdown
        height={45}
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryCurrencyCodeLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryCurrencyCodePlaceholder'
        )}
        value={formik?.values.salaryCurrencyCode}
        setFieldValue={formik?.setFieldValue}
        setFieldTouched={formik?.setFieldTouched}
        name="salaryCurrencyCode"
        options={[
          { value: 'USD', label: 'USD' },
          { value: 'CAD', label: 'CAD' },
          { value: 'GBP', label: 'GBP' },
          { value: 'EUR', label: 'EUR' },
          { value: 'AUD', label: 'AUD' },
          { value: 'INR', label: 'INR' },
        ]}
        touched={isTriedToSubmit}
        error={formik?.errors.salaryCurrencyCode}
      />
    ) : (
      <TextInput
        readonly
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryCurrencyCodeLabel'
        )}
        height={45}
        value={data?.salaryCurrencyCode || ''}
      />
    );
  };

  const isExactSalary =
    formik &&
    (formik?.values?.salaryType?.value === 'Fixed' ||
      formik?.values?.salaryType === 'Fixed');

  const isNegotiableSalary =
    formik &&
    (formik?.values?.salaryType?.value === 'Negotiable' ||
      formik?.values?.salaryType === 'Negotiable');

  const expectedSalaryToField = () => {
    return formik && !readOnly ? (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Expected Salary To'
        )}
        height={45}
        note={t('common.optional')}
        value={formik?.values.salaryTo}
        name="salaryTo"
        onChange={(event) =>
          formik?.setFieldValue(
            'salaryTo',
            event.target.value.replace(/\D/g, '')
          )
        }
        touched={isTriedToSubmit}
        error={formik?.errors.salaryTo}
      />
    ) : (
      <TextInput
        note={t('common.optional')}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Expected Salary To'
        )}
        readonly
        height={45}
        value={formik?.values.salaryTo || data?.salaryTo}
      />
    );
  };

  const expectedSalaryFromField = () => {
    return formik && !readOnly ? (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Expected Salary From'
        )}
        height={45}
        note={t('common.optional')}
        value={formik?.values.salaryFrom}
        name="salaryFrom"
        onChange={(event) =>
          formik?.setFieldValue(
            'salaryFrom',
            event.target.value.replace(/\D/g, '')
          )
        }
        touched={isTriedToSubmit}
        error={formik?.errors.salaryFrom}
      />
    ) : (
      <TextInput
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.Expected Salary From'
        )}
        note={t('common.optional')}
        readonly
        height={45}
        value={formik?.values.salaryFrom || data?.salaryFrom}
      />
    );
  };

  const benefitsField = () => {
    return formik && !readOnly ? (
      <ChipsDropdown
        readOnly={readOnly}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitLabel'
        )}
        width="100%"
        maxLength={10}
        height={186}
        value={formik?.values.jobBenefitIds}
        setFieldValue={formik?.setFieldValue}
        name="jobBenefitIds"
        infoWidth={137}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitInfo'
        )}
        note={t('common.optional')}
        searchInfo={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitSearchInfo'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitPlaceholder'
        )}
        searchInfoWidth={186}
        fetchOptions={{
          dataName: 'jobBenefitsOptionsBasicInfo',
          getDataHandler: ({ pageNumber, term }) =>
            AdminService.getJobBenefits({
              orderBy: 'sortOrder',
              sortOrder: 'asc',
              term,
              pageNumber,
            }),
        }}
        touched={isTriedToSubmit}
        error={formik?.errors.jobBenefitIds}
        errorStyle={{ position: 'absolute', bottom: -27 }}
      />
    ) : (
      <ChipsReadonly
        values={
          formik?.values.jobBenefitIds ||
          data?.jobBenefits?.map((benefit) => benefit.name)
        }
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitLabel'
        )}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobBenefitInfo'
        )}
        infoWidth={137}
      />
    );
  };

  const coverField = () => {
    return formik && !readOnly ? (
      <MediaUploader
        error={formik?.errors.coverFileName}
        touched={isTriedToSubmit}
        isEdit={isEdit}
        type="image"
        file={formik?.values.coverFileName}
        setFile={(file) => formik?.setFieldValue('coverFileName', file)}
      />
    ) : (
      <div
        style={{
          visibility:
            formik?.values.coverFileName || data?.coverFileName
              ? 'visible'
              : 'hidden',
        }}
      >
        <img
          className={classes.savedImage}
          src={getResizedImageUrl({
            url: `jobpostcover/${
              formik?.values.coverFileName || data?.coverFileName
            }`,
            width: 400,
            quality: 70,
          })}
          alt="jobpostcover"
        />
      </div>
    );
  };

  const videoField = () => {
    return formik && !readOnly ? (
      <MediaUploader
        isEdit={isEdit}
        type="video"
        file={formik?.values.jobPostVideoId}
        setFile={changeVideo}
      />
    ) : (
      <div className={classes.savedVideo}>
        <video
          src={formik?.values.jobPostVideo?.url || data?.jobPostVideo?.url}
        />
        <div className={classes.text}>
          <span>
            {t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.JobSpecsVideoLabel'
            )}{' '}
            <br /> {t('common.video')}
          </span>
          <span>({t('common.optional')})</span>
        </div>
      </div>
    );
  };

  const noteField = () => {
    return formik ? (
      <Textarea
        readonly={readOnly}
        height={186}
        maxLength={5000}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.NotesLabel'
        )}
        placeholder={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.NotesPlaceholder'
        )}
        value={formik?.values.note}
        onChange={formik?.handleChange}
        error={formik?.errors.note}
        name="note"
        touched={isTriedToSubmit}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.InternalNotesInfo'
        )}
        infoWidth={142}
        note={t('common.optional')}
      />
    ) : (
      <Textarea
        height={186}
        label={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.NotesLabel'
        )}
        readonly
        value={data.note}
        info={t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.InternalNotesInfo'
        )}
        infoWidth={142}
      />
    );
  };

  const docUploaderField = () => {
    return formik && !readOnly ? (
      <DocUploader
        file={formik?.values.jobPostAttachmentIds}
        setFile={(file) => formik?.setFieldValue('jobPostAttachmentIds', file)}
      />
    ) : (
      <DocUploader
        readonly
        filename={formik?.values.jobPostAttachments?.[0]?.displayName}
      />
    );
  };

  const salaryInfoField = () => {
    const salaryTypeValue =
      (formik?.values?.salaryType &&
        (formik?.values?.salaryType?.value || formik?.values?.salaryType)) ||
      data?.salaryType;

    let infoKey =
      'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryInfo';
    if (salaryTypeValue === 'Range') {
      infoKey =
        'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryInfoRange';
    } else if (salaryTypeValue === 'Fixed') {
      infoKey =
        'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryInfoFixed';
    } else if (salaryTypeValue === 'Negotiable') {
      infoKey =
        'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryInfoNegotiable';
    }

    return <span className={classes.salaryInfoNote}>{t(infoKey)}</span>;
  };

  const salaryPreviewField = () => {
    const parseNumber = (val) => {
      if (val === null || val === undefined || val === '') return null;
      const num = Number(String(val).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(num) ? num : null;
    };

    const getValue = (fieldName) => {
      if (formik) {
        return formik?.values?.[fieldName] ?? data?.[fieldName];
      }
      return data?.[fieldName];
    };

    const getSelectedValue = (fieldName) => {
      const fromFormik = formik?.values?.[fieldName];
      if (fromFormik) {
        return typeof fromFormik === 'string' ? fromFormik : fromFormik?.value;
      }
      const fromData = data?.[fieldName];
      return typeof fromData === 'string' ? fromData : fromData?.value;
    };

    const salaryTypeValue = getSelectedValue('salaryType');
    const currencyCode = getSelectedValue('salaryCurrencyCode') || 'USD';
    const frequencyValue = getSelectedValue('salaryFrequency');

    const salaryFrom = parseNumber(getValue('salaryFrom'));
    const salaryTo = parseNumber(getValue('salaryTo'));

    const formatMoney = (amount) => {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: 0,
        }).format(amount);
      } catch (e) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(amount);
      }
    };

    const getFrequencyLabel = () => {
      if (!frequencyValue) return '';
      return t(
        `dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFrequencyOptions.${frequencyValue}`
      );
    };

    const frequencyLabel = getFrequencyLabel();

    if (salaryTypeValue === 'Range') {
      if (!salaryFrom && !salaryTo) return null;
      if (salaryFrom && !salaryTo) {
        const fromLabel = t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryFromLabel'
        );
        return (
          <span className={classes.salaryInfoNotePreview}>
            {fromLabel} {formatMoney(salaryFrom)}
            {frequencyLabel ? ` / ${frequencyLabel}` : ''}
          </span>
        );
      }
      if (!salaryFrom && salaryTo) {
        const upToLabel = t(
          'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryUpToLabel'
        );
        return (
          <span className={classes.salaryInfoNotePreview}>
            {upToLabel} {formatMoney(salaryTo)}
            {frequencyLabel ? ` / ${frequencyLabel}` : ''}
          </span>
        );
      }
      if (salaryFrom && salaryTo) {
        return (
          <span className={classes.salaryInfoNotePreview}>
            {formatMoney(salaryFrom)} – {formatMoney(salaryTo)}
            {frequencyLabel ? ` / ${frequencyLabel}` : ''}
          </span>
        );
      }
      return null;
    }

    if (salaryTypeValue === 'Fixed') {
      if (!salaryTo) return null;
      return (
        <span className={classes.salaryInfoNotePreview}>
          {formatMoney(salaryTo)}
          {frequencyLabel ? ` / ${frequencyLabel}` : ''}
        </span>
      );
    }

    if (salaryTypeValue === 'Negotiable') {
      if (!salaryTo) return null;
      const negotiableLabel = t(
        'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalaryTypeOptions.Negotiable'
      );
      return (
        <span className={classes.salaryInfoNotePreview}>
          {negotiableLabel}
          {frequencyLabel ? ` / ${frequencyLabel}` : ''}
        </span>
      );
    }

    return null;
  };

  return (
    <div className={classes.BasicInfo}>
      <div className={classes.formSection}>
        <div className={classes.formSectionTitle}>
          {t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.GeneralSectionTitle'
          )}
        </div>
        <div className={classes.formItem2col}>{jobOwnerField}</div>
        <div className={classes.formItem2col}>{jobPostTypeField()}</div>
        <div className={classes.formItem2col}>{jobTitleField()}</div>
        <div className={classes.formItem2col}>{corporateTitleField()}</div>
      </div>

      <div className={classes.formSection}>
        <div className={classes.formSectionTitle}>
          {t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.RoleDetailsSectionTitle'
          )}
        </div>
        <div className={classes.formItem2col}>{jobTypeField()}</div>
        <div className={classes.formItem2col}>{jobLocationTypeField()}</div>
        <div className={classes.formItemFull}>{jobDescriptionField()}</div>
      </div>

      <div className={classes.formSection}>
        <div className={classes.formSectionTitle}>
          {t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.RequirementsSectionTitle'
          )}
        </div>
        <div className={classes.formItem2col}>{educationField()}</div>
        <div className={classes.formItem2col}>{experienceField()}</div>
        <div className={classes.formItemFull}>{skillField()}</div>
      </div>

      {formik && 'company' in formik?.values && (
        <div className={classes.formSection}>
          <div className={classes.formSectionTitle}>
            {t(
              'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.EmployerDetailsSectionTitle'
            )}
          </div>
          <div className={classes.formItemFull}>{companyField()}</div>
          {!isInterviewer && (
            <div className={classes.formItemFull}>{companyManagerField()}</div>
          )}
          {formik && formik?.values?.company && !isInterviewer && (
            <div className={classes.formItemFull}>
              {companyLocationFormField()}
            </div>
          )}
          {!formik && !isInterviewer && (
            <div className={classes.formItemFull}>
              {companyLocationLabelField()}
            </div>
          )}
          <div className={classes.formItemFull}>
            {companyJoiningNoteField()}
          </div>
          <div className={classes.formItemFull}>
            {companyDescriptionField()}
          </div>
          {formik && !isInterviewer && !isClient && (
            <div className={classes.formItemFull}>{hideCompanyFormField()}</div>
          )}
          {!formik && !isInterviewer && !isClient && (
            <div className={classes.formItemFull}>
              {hideCompanyLabelField()}
            </div>
          )}
        </div>
      )}
      {formik &&
        'salaryTo' in formik?.values &&
        !isInterviewer &&
        !isClient && (
          <div className={classes.formSection}>
            <div className={classes.formSectionTitle}>
              {t(
                'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.SalarySectionTitle'
              )}
            </div>

            <div className={classes.formItemFull}>
              <div className={classes.salaryRow}>
                <div className={classes.salaryCol}>{salaryTypeField()}</div>
                <div className={classes.salaryCol}>
                  {salaryCurrencyCodeField()}
                </div>

                {!isExactSalary && !isNegotiableSalary && (
                  <div className={classes.salaryCol}>
                    {' '}
                    {expectedSalaryFromField()}
                  </div>
                )}

                {!isNegotiableSalary && (
                  <div className={classes.salaryCol}>
                    {expectedSalaryToField()}
                  </div>
                )}
                <div className={classes.salaryCol}>
                  {salaryFrequencyField()}
                </div>
              </div>
              <div className={classes.salaryInfoRow}>
                <div className={classes.salaryCol}>{salaryInfoField()}</div>
                <div className={classes.salaryInfoCol}>
                  {salaryPreviewField()}
                </div>
              </div>
            </div>
            <div className={classes.formItemFull}>{benefitsField()}</div>
          </div>
        )}
      <div className={classes.formSection}>
        <div className={classes.formSectionTitle}>
          {t(
            'dashboardComponents.Console.JobManagement.JobTemplates.Template.BasicInfo.MediasSectionTitle'
          )}
        </div>
        <div
          style={{
            display:
              (formik && !readOnly) || data?.coverFileName?.length > 0
                ? 'block'
                : 'none',
          }}
          className={classes.formItem2col}
        >
          {coverField()}
        </div>
        <div
          style={{
            display:
              (formik && !readOnly) || data?.coverFileName?.length > 0
                ? 'block'
                : 'none',
          }}
          className={classes.formItem2col}
        >
          {videoField()}
        </div>
        <div className={classes.formItemFull}>{noteField()}</div>
        <div className={classes.formItemFull}>{docUploaderField()}</div>
      </div>
    </div>
  );
}
